import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminTable from "../../components/Admin/AdminTable";
import ConfirmModal from "../../components/common/ConfirmModal";
import toast from "react-hot-toast";
import adminApi from "../../services/adminApi";
import { buildAppPath } from "../../utils/constants";
import "../../styles/Admin.css";

const UsersTable = ({ users, loading, onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [suspendingId, setSuspendingId] = useState(null);
  const [confirmSuspend, setConfirmSuspend] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);

  useEffect(() => {
    // Get current admin email
    adminApi
      .get("/admin/me")
      .then((res) => setAdminEmail(res.data.email))
      .catch(() => console.error("Failed to get admin email"));
  }, []);

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(user.id).includes(searchTerm) ||
    (user.whmcs_user_id && user.whmcs_user_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const impersonate = async (id) => {
    setImpersonatingId(id);
    try {
      const { data } = await adminApi.post(`/admin/impersonate/${id}`);
      localStorage.setItem("token", data.token);
      toast.success("Impersonation started - redirecting...");
      setTimeout(() => {
        window.location.href = buildAppPath("/");
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to impersonate user");
      setImpersonatingId(null);
    }
  };

  const handleSuspendClick = (user) => {
    // Check if trying to suspend themselves
    if (adminEmail && user.email.toLowerCase() === adminEmail.toLowerCase()) {
      toast.error("You cannot suspend your own account");
      return;
    }
    setConfirmSuspend(user);
  };

  const confirmSuspendUser = async () => {
    if (!confirmSuspend) return;
    const userId = confirmSuspend.id;
    setSuspendingId(userId);
    setConfirmSuspend(null);
    try {
      await adminApi.post(`/admin/users/${userId}/suspend`);
      toast.success("User suspended successfully");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to suspend user");
    } finally {
      setSuspendingId(null);
    }
  };

  const unsuspendUser = async (id) => {
    setSuspendingId(id);
    try {
      await adminApi.post(`/admin/users/${id}/unsuspend`);
      toast.success("User unsuspended successfully");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to unsuspend user");
    } finally {
      setSuspendingId(null);
    }
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "email", label: "Email" },
    {
      key: "whmcs_user_id",
      label: "WHMCS ID",
      render: (value) => value || "-",
    },
    {
      key: "suspended",
      label: "Status",
      render: (value, row) => {
        if (value) {
          return <span className="admin-badge admin-badge-suspended">Suspended</span>;
        }
        return (
          <span className={row.is_active ? "admin-badge admin-badge-active" : "admin-badge admin-badge-inactive"}>
            {row.is_active ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  const actions = (row) => (
    <>
      <button
        className="btn-secondary admin-action-btn"
        onClick={() => navigate(`/admin/users/${row.id}`)}
        title="View user details"
      >
        View
      </button>
      <button
        className="btn-primary admin-action-btn"
        onClick={() => impersonate(row.id)}
        disabled={impersonatingId === row.id || impersonatingId !== null}
        title="Impersonate user"
      >
        {impersonatingId === row.id ? "..." : "Impersonate"}
      </button>
      {row.suspended ? (
        <button
          className="btn-primary admin-action-btn"
          onClick={() => unsuspendUser(row.id)}
          disabled={suspendingId === row.id || suspendingId !== null}
          title="Unsuspend user"
        >
          {suspendingId === row.id ? "..." : "Unsuspend"}
        </button>
      ) : (
        <button
          className="btn-danger admin-action-btn"
          onClick={() => handleSuspendClick(row)}
          disabled={
            suspendingId === row.id ||
            suspendingId !== null ||
            (adminEmail && row.email.toLowerCase() === adminEmail.toLowerCase())
          }
          title={
            adminEmail && row.email.toLowerCase() === adminEmail.toLowerCase()
              ? "Cannot suspend your own account"
              : "Suspend user"
          }
        >
          {suspendingId === row.id ? "..." : "Suspend"}
        </button>
      )}
    </>
  );

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Users</h3>
      </div>

      <div className="admin-search">
        <input
          type="text"
          className="form-control"
          placeholder="Search by email, ID, or WHMCS ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <AdminTable
        columns={columns}
        data={filteredUsers}
        actions={actions}
        loading={loading}
        emptyMessage="No users found"
      />

      <ConfirmModal
        show={!!confirmSuspend}
        title="Suspend User"
        message={`Are you sure you want to suspend "${confirmSuspend?.email}"? They will not be able to login until unsuspended.`}
        confirmText="Suspend"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmSuspendUser}
        onClose={() => setConfirmSuspend(null)}
      />
    </div>
  );
};

export default UsersTable;

